import { type ChangeEvent, type FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Typography from 'app/ui/components/atoms/typography';
import PillElement from 'app/ui/components/atoms/pill';

import { Fieldset } from 'app/ui/components/molecules/field-set-styled';

export type PillData = {
  id: number | string;
  name: string;
};

export type Pill = PillData & {
  isChecked: boolean;
};

export type Pills = Pill[];

export type PillOnChangeEvent = (event: ChangeEvent<HTMLInputElement>) => void;

export type PillVariant = 'wrap' | 'inline';

export interface PillFieldSetProps {
  pills: Pills;
  label: string;
  onChange: PillOnChangeEvent;
  disabled?: boolean;
  className?: string;
  variant?: PillVariant;
}

const ScrollWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Gradient = styled.div<{ $visible: boolean; $isLeft?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 60px;
  pointer-events: none;
  z-index: 1;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${({ $visible }) => ($visible ? '0.3s' : '0s')} ease;
  
  ${({ $isLeft, theme }) =>
    $isLeft
      ? `
    left: 0;
    background: linear-gradient(to left, rgba(0, 0, 0, 0), ${theme.colors.black});
  `
      : `
    right: 0;
    background: linear-gradient(to right, rgba(0, 0, 0, 0), ${theme.colors.black});
  `}
`;

const PillFieldset = styled(Fieldset)<{ $variant: PillVariant }>`
  display: flex;
  flex-direction: row;
  margin: 1rem 0;

  ${({ $variant }) =>
    $variant === 'inline'
      ? `
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch; /* Enable smooth scrolling on iOS */
        
        /* Hide scrollbar */
        -ms-overflow-style: none;
        scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }

        & > * {
          flex-shrink: 0;
          white-space: nowrap;
        }
      `
      : `
          flex-wrap: wrap;
        `}

  ${({ theme: { spacing, media } }) => `
    ${media.medium(
      () => `
        gap: ${spacing(1)};
      `,
    )}
  `}
`;

export const PillFieldSet: FC<PillFieldSetProps> = ({
  pills,
  label,
  onChange,
  disabled = false,
  className,
  variant = 'wrap',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [showLeftGradient, setShowLeftGradient] = useState(false);

  const checkScroll = useCallback(() => {
    if (variant !== 'inline' || !scrollRef.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const isScrollable = scrollWidth > clientWidth;

    // Check for right gradient visibility
    const isAtEnd = Math.ceil(scrollLeft + clientWidth) >= scrollWidth;
    setShowRightGradient(isScrollable && !isAtEnd);

    // Check for left gradient visibility
    // Show if we have scrolled away from start (scrollLeft > 0)
    setShowLeftGradient(isScrollable && scrollLeft > 0);
  }, [variant]);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll]);

  // Re-check when pills change as it affects content width
  useEffect(() => {
    if (pills) {
      checkScroll();
    }
  }, [pills, checkScroll]);

  const content = (
    <PillFieldset className={className} $variant={variant} ref={scrollRef} onScroll={checkScroll}>
      {pills.map(
        ({ id, name, isChecked }) =>
          id &&
          name && (
            <PillElement
              id={id}
              name={name}
              isChecked={isChecked}
              disabled={disabled}
              onChange={(e) => !disabled && onChange(e)}
              key={`${id}-${name}`}
            />
          ),
      )}
    </PillFieldset>
  );

  return (
    <Fieldset>
      <Typography variant="body2">{label}</Typography>
      {variant === 'inline' ? (
        <ScrollWrapper>
          <Gradient $visible={showLeftGradient} $isLeft={true} />
          {content}
          <Gradient $visible={showRightGradient} />
        </ScrollWrapper>
      ) : (
        content
      )}
    </Fieldset>
  );
};
